import React from 'react';
import { buttonClickEvent } from 'analytics/events/studio';
import { toPlainText } from '@portabletext/react';
import { Typography, getTypographyDefaultColor, Button, Space, Col, Row, Container } from '@ag1/ui';
import clsx from 'clsx';
import { BenefitsJourneyV4, SanityKeyed } from 'types/studio/studio';
import { getSectionPaddingClasses } from 'shared/utils/getSectionPaddingClasses';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import { BenefitsJourneyCardV4 } from './BenefitsJourneyCardV4';
import CustomPortableText from '../PortableText/PortableText';
import styles from './BenefitsJourneyV4.module.scss';
import stylesCard from './BenefitsJourneyCardV4.module.scss';
import { CommonSectionProps } from '../types';

const BenefitsJourneyV4Markup = ({
  _key: key,
  _type: sectionType,
  headline,
  introText,
  cards,
  reverse,
  baseButtonLink,
  theme,
  addButton,
  bgColor,
  textAlignment = 'center',
  padding,
  visibleOnPageLoad
}: SanityKeyed<BenefitsJourneyV4> & CommonSectionProps) => (
  <div
    className={clsx(
      bgColor === 'stone' ? styles.bgStone : styles.bgWhite,
      getThemeCSSClass(theme)
    )}>
    <Container narrow id={key} data-testid={sectionType}>
      <Space
        top={{ xxs: 40, s: 60, l: 100 }}
        bottom={{ xxs: 40, s: 60, l: 100 }}
        className={clsx(
          styles.root,
          textAlignment === 'left' && styles.left,
          getSectionPaddingClasses(styles, padding)
        )}>
        <Typography
          variant="headlineSans"
          size="2"
          component="h2"
          noPadding
          color={getTypographyDefaultColor(theme)}>
          {headline}
        </Typography>
        {introText && (
          <Typography
            variant="body"
            size="1"
            color={getTypographyDefaultColor(theme)}
            className={clsx(styles.intro, textAlignment === 'center' && styles.body)}
            noPadding>
            {introText}
          </Typography>
        )}
        <Space top={{ xxs: 32, s: 40, m: 48, l: 56 }} className={styles.wrap}>
          <Row>
            {cards.map((card, index) => {
              const { _key: cardKey } = card;
              return (
                <Col
                  key={cardKey}
                  span={{ xxs: 12, l: 4 }}
                  className={clsx(
                    styles.card,
                    index % 2 === 1 && reverse ? stylesCard.reverse : ''
                  )}>
                  <BenefitsJourneyCardV4
                    {...card}
                    sectionType={sectionType}
                    headline={headline}
                    theme={theme}
                    textAlignment={textAlignment}
                    visibleOnPageLoad={visibleOnPageLoad}
                  />
                </Col>
              );
            })}
          </Row>
        </Space>
        {baseButtonLink?.btnLink && addButton && (
          <Space top={{ xxs: 40, s: 50 }} className={styles.btnWrap}>
            <Button
              size="medium"
              rounded
              theme={theme}
              icon={baseButtonLink.showIcon && baseButtonLink.iconOptions?.icon}
              iconPosition={baseButtonLink.iconOptions?.position}
              className={styles.button}
              href={
                baseButtonLink.btnLink.external || baseButtonLink.btnLink.internal?.slug?.current
              }
              target={baseButtonLink?.targetBlank ? '_blank' : '_self'}
              data-gtm-skip-global
              data-testid={sectionType}
              onClick={() =>
                buttonClickEvent({
                  location: sectionType,
                  clickText: baseButtonLink.portableText
                    ? toPlainText(baseButtonLink.portableText)
                    : undefined,
                  url:
                    baseButtonLink?.btnLink?.external ||
                    baseButtonLink?.btnLink?.internal?.slug?.current
                })
              }>
              {baseButtonLink.portableText && (
                <CustomPortableText value={baseButtonLink.portableText} />
              )}
            </Button>
          </Space>
        )}
      </Space>
    </Container>
  </div>
);

export default BenefitsJourneyV4Markup;
